/**
 * ThemeColorsCard — Card compartilhado para vendedor/cliente personalizar cores do próprio portal.
 * Apenas 2 controles: cor de fundo + cor dos botões. Nada de banner/grid/logo.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Palette, RotateCcw, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Presets prontos (nome, bg, accent)
const PRESETS = [
  { name: 'Padrão',     bg: '',         accent: '' },
  { name: 'Menta',      bg: '#e8f7f1',  accent: '#059669' },
  { name: 'Azul Marinho', bg: '#0f172a', accent: '#38bdf8' },
  { name: 'Rosé',       bg: '#fdf2f8',  accent: '#db2777' },
  { name: 'Terra',      bg: '#f5f1eb',  accent: '#a16207' },
  { name: 'Grafite',    bg: '#1f2937',  accent: '#f97316' },
];

export default function ThemeColorsCard({ endpoint, initialColors, onSaved, testidPrefix = 'theme' }) {
  const [bg, setBg] = useState(initialColors?.bg || '');
  const [accent, setAccent] = useState(initialColors?.accent || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}${endpoint}`, { bg, accent });
      toast.success('Cores atualizadas!');
      // Aplica imediatamente no root
      applyThemeColors(res.data.theme_colors);
      onSaved && onSaved(res.data.theme_colors);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar cores');
    }
    setSaving(false);
  };

  const handleReset = async () => {
    setBg(''); setAccent('');
    try {
      const res = await axios.put(`${API}${endpoint}`, { bg: '', accent: '' });
      toast.success('Cores restauradas ao padrão');
      applyThemeColors({});
      onSaved && onSaved({});
    } catch { /* ignore */ }
  };

  const applyPreset = (p) => { setBg(p.bg); setAccent(p.accent); };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5" data-testid={`${testidPrefix}-colors-card`}>
      <div className="flex items-center gap-2 mb-1">
        <Palette className="w-5 h-5 text-indigo-600" />
        <h3 className="font-bold text-gray-900 dark:text-white">Personalizar Cores</h3>
      </div>
      <p className="text-xs text-gray-500 mb-4">Escolha a cor de fundo do seu painel e a cor dos botões. Apenas você vê essas mudanças.</p>

      {/* Presets */}
      <div className="mb-4">
        <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-2 font-semibold">Temas prontos</p>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map(p => (
            <button
              key={p.name}
              type="button"
              onClick={() => applyPreset(p)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:border-indigo-500 text-xs font-medium transition-colors"
              data-testid={`${testidPrefix}-preset-${p.name.toLowerCase()}`}
            >
              {p.bg && <span className="w-3 h-3 rounded-full border" style={{ background: p.bg }} />}
              {p.accent && <span className="w-3 h-3 rounded-full border" style={{ background: p.accent }} />}
              {!p.bg && !p.accent && <span className="w-3 h-3 rounded-full border bg-gradient-to-br from-white to-gray-300" />}
              <span>{p.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Custom pickers */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold mb-1 text-gray-700 dark:text-gray-300">Cor de fundo</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={bg || '#ffffff'}
              onChange={(e) => setBg(e.target.value)}
              className="w-10 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
              data-testid={`${testidPrefix}-bg-picker`}
            />
            <input
              type="text"
              value={bg}
              onChange={(e) => setBg(e.target.value)}
              placeholder="#ffffff"
              className="flex-1 text-sm px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900"
              maxLength={20}
              data-testid={`${testidPrefix}-bg-input`}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-gray-700 dark:text-gray-300">Cor dos botões</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={accent || '#2563eb'}
              onChange={(e) => setAccent(e.target.value)}
              className="w-10 h-10 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
              data-testid={`${testidPrefix}-accent-picker`}
            />
            <input
              type="text"
              value={accent}
              onChange={(e) => setAccent(e.target.value)}
              placeholder="#2563eb"
              className="flex-1 text-sm px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900"
              maxLength={20}
              data-testid={`${testidPrefix}-accent-input`}
            />
          </div>
        </div>
      </div>

      {/* Preview */}
      <div
        className="mt-4 p-4 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-between transition-colors"
        style={{ background: bg || undefined }}
      >
        <span className="text-xs font-semibold" style={{ color: bg ? getContrastText(bg) : undefined }}>
          Preview do painel
        </span>
        <button
          type="button"
          className="px-3 py-1.5 rounded text-sm font-semibold text-white transition-transform hover:scale-105"
          style={{ background: accent || '#2563eb' }}
        >
          Botão
        </button>
      </div>

      <div className="flex gap-2 mt-4">
        <button
          type="button"
          onClick={handleReset}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
          data-testid={`${testidPrefix}-reset`}
        >
          <RotateCcw className="w-4 h-4" /> Restaurar padrão
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded disabled:opacity-50"
          data-testid={`${testidPrefix}-save`}
        >
          <Save className="w-4 h-4" /> {saving ? 'Salvando...' : 'Salvar Cores'}
        </button>
      </div>
    </div>
  );
}

// ============ HELPERS ============
export function applyThemeColors(colors) {
  const root = document.documentElement;
  const hasBg = !!colors?.bg;
  if (hasBg) {
    root.style.setProperty('--portal-bg', colors.bg);
    root.classList.add('has-portal-theme');
  } else {
    root.style.removeProperty('--portal-bg');
    root.classList.remove('has-portal-theme');
  }
  if (colors?.accent) root.style.setProperty('--portal-accent', colors.accent);
  else root.style.removeProperty('--portal-accent');
}

// Calcula texto contraste (branco ou preto) baseado na luminância do bg
export function getContrastText(bg) {
  try {
    const c = bg.replace('#', '');
    const r = parseInt(c.substr(0, 2), 16);
    const g = parseInt(c.substr(2, 2), 16);
    const b = parseInt(c.substr(4, 2), 16);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.55 ? '#111' : '#fff';
  } catch { return '#111'; }
}
